import { resolvePrice } 			from '../../../shared/utils/productUtils.js'
import { sumSunLightRanges } 	from '../../../shared/utils/sunLightUtils.js'
import { STATUS_PRIORITY } 		from '../../../shared/constants/availabilityStatus.js';

import { SORT_PARAMETER, SORT_DIRECTION } from '../constants/sortParameters.js'

/**
 * Sort direction options for the direction Select.
 * Values match SortLevel.direction.
 */
export const SORT_DIRECTION_OPTIONS = [
	{ value: SORT_DIRECTION.ASC, labelKey: 'catalog.sort_asc' },
	{ value: SORT_DIRECTION.DESC, labelKey: 'catalog.sort_desc' },
];

/**
 * Context passed to `isSupported` during capabilities derivation.
 *
 * @typedef {Object} SortSupportCapabilities
 * @property {boolean} [pricesVisible] - Whether prices are globally enabled in CMS/settings.
 * @property {boolean} [favoritesSectionEnabled] - Whether favorites action is active in the project.
 * @property {boolean} [hasInsolationData] - Whether dataset contains insolation ranges.
 */
/**
 * Context passed to `compare` during sort execution.
 *
 * @typedef {Object} SortCompareContext
 * @property {function(string): boolean} [isFavorite] - Resolver checking if product UUID is favorited.
 * @property {Map<string, string>|null} [areaDecileMap] - Precomputed decile bucket map (UUID -> 'p0'..'p9').
 */
/**
 * SORT_REGISTRY — Declarative single source of truth for all catalog sorting parameters.
 *
 * Shape per entry:
 * @typedef {Object} SortRegistryEntry
 * @property {string} id - Unique identifier matching one of `SORT_PARAMETER` keys.
 * @property {string} labelKey - i18n translation key used for UI dropdowns (`catalog.*` or `common.*`).
 * @property {function(SortSupportCapabilities=): boolean} [isSupported] - Pure guard function returning whether this sort option is available.
 * @property {function(Object, Object, SortCompareContext=): number} compare - Pure comparator function comparing two products in ascending order.
 */
export const SORT_REGISTRY = [
	{
    id: SORT_PARAMETER.STATUS,
    labelKey: 'catalog.status',
    isSupported: () => true,
    compare: (a, b) => (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99),
  },
  {
    id: SORT_PARAMETER.OFFER,
    labelKey: 'catalog.offer',
    isSupported: () => true,
    compare: (a, b) => (a.price_offer?.on_offer ? 0 : 1) - (b.price_offer?.on_offer ? 0 : 1),
  },
  {
    id: SORT_PARAMETER.ROOMS,
    labelKey: 'catalog.rooms',
    isSupported: () => true,
    compare: (a, b) => (a.rooms ?? 0) - (b.rooms ?? 0),
  },
  {
    id: SORT_PARAMETER.AREA,
    labelKey: 'catalog.area',
    isSupported: () => true,
    compare: (a, b) => (parseFloat(a.area_original) || 0) - (parseFloat(b.area_original) || 0),
  },
  {
    id: SORT_PARAMETER.PRICE,
    labelKey: 'catalog.price',
    isSupported: (capabilities) => capabilities?.pricesVisible !== false,
    compare: (a, b) => (resolvePrice(a) ?? 0) - (resolvePrice(b) ?? 0),
  },
  {
    id: SORT_PARAMETER.TIERS,
    labelKey: 'common.floor',
    isSupported: () => true,
    compare: (a, b) => (a.ref_tier ?? 0) - (b.ref_tier ?? 0),
  },
  {
    id: SORT_PARAMETER.FAVORITES,
    labelKey: 'catalog.favorites',
    isSupported: (capabilities) => capabilities?.favoritesSectionEnabled !== false,
    compare: (a, b, { isFavorite } = {}) => {
      const favFn = typeof isFavorite === 'function' ? isFavorite : () => false
      return (favFn(a.uuid) ? 0 : 1) - (favFn(b.uuid) ? 0 : 1)
    },
  },
  {
    id: SORT_PARAMETER.INSOLATION,
    labelKey: 'catalog.insolation',
    isSupported: (capabilities) => !!capabilities?.isInsolationSupported,
    compare: (a, b) => sumSunLightRanges(a.sun_light_ranges) - sumSunLightRanges(b.sun_light_ranges),
  },
  {
    id: SORT_PARAMETER.COMPARE_AREA,
    labelKey: 'catalog.compare_area',
    isSupported: () => true,
    compare: (a, b, { areaDecileMap } = {}) => {
      const bucketA = areaDecileMap?.get(a.uuid) ?? 'p0'
      const bucketB = areaDecileMap?.get(b.uuid) ?? 'p0'
      return bucketA.localeCompare(bucketB)
    },
  },
  {
    id: SORT_PARAMETER.BUILDING,
    labelKey: 'common.building',
    isSupported: (capabilities) => capabilities?.hasMultipleBuildings === true,
    compare: (a, b) => (a.building_id ?? '').localeCompare(b.building_id ?? '')
  },
  {
    id: SORT_PARAMETER.GATE,
    labelKey: 'common.gate',
    isSupported: (capabilities) => capabilities?.hasMultipleGates === true,
    compare: (a, b) => (a.gate ?? '').localeCompare(b.gate ?? '')
  },
  {
    id: SORT_PARAMETER.ID_CAD,
    labelKey: 'catalog.sort_number',
    isSupported: () => true,
    compare: (a, b) => (a.id_cad ?? '').localeCompare(b.id_cad ?? ''),
  },
]

/**
 * Helper to retrieve a single sort entry by parameter ID.
 *
 * @param {string} paramId
 * @returns {SortRegistryEntry|undefined}
 */
export function getSortEntry(paramId) {
  return SORT_REGISTRY.find((entry) => entry.id === paramId)
}